import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api.service';

interface SubcontractState {
    subcontracts: any[];
    currentSubcontract: any | null;
    paymentCertificates: any[];
    currentPaymentCertificate: any | null;
    isLoading: boolean;
    error: string | null;
}

const initialState: SubcontractState = {
    subcontracts: [],
    currentSubcontract: null,
    paymentCertificates: [],
    currentPaymentCertificate: null,
    isLoading: false,
    error: null,
};

export const fetchSubcontracts = createAsyncThunk(
    'sc/fetchSubcontracts',
    async (params: any | undefined, { rejectWithValue }) => {
        try {
            const response = await apiService.listSubcontracts(params);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const fetchSubcontractById = createAsyncThunk(
    'sc/fetchSubcontractById',
    async (id: number, { rejectWithValue }) => {
        try {
            const response = await apiService.getSubcontract(id);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const createSubcontract = createAsyncThunk(
    'sc/createSubcontract',
    async (data: any, { rejectWithValue }) => {
        try {
            const response = await apiService.createSubcontract(data);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const fetchPaymentCertificates = createAsyncThunk(
    'sc/fetchPaymentCertificates',
    async (scId: number, { rejectWithValue }) => {
        try {
            const response = await apiService.getPaymentCertificates(scId);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

export const fetchPaymentCertificateById = createAsyncThunk(
    'sc/fetchPaymentCertificateById',
    async ({ scId, pcId }: { scId: number; pcId: number }, { rejectWithValue }) => {
        try {
            const response = await apiService.getPaymentCertificateById(scId, pcId);
            return response.data;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message);
        }
    }
);

const scSlice = createSlice({
    name: 'sc',
    initialState,
    reducers: {
        clearCurrentSubcontract: (state) => {
            state.currentSubcontract = null;
        },
        clearCurrentPaymentCertificate: (state) => {
            state.currentPaymentCertificate = null;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch Subcontracts
        builder.addCase(fetchSubcontracts.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchSubcontracts.fulfilled, (state, action) => {
            state.isLoading = false;
            state.subcontracts = action.payload;
        });
        builder.addCase(fetchSubcontracts.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Fetch Subcontract by ID
        builder.addCase(fetchSubcontractById.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchSubcontractById.fulfilled, (state, action) => {
            state.isLoading = false;
            state.currentSubcontract = action.payload;
        });
        builder.addCase(fetchSubcontractById.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Create Subcontract
        builder.addCase(createSubcontract.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(createSubcontract.fulfilled, (state, action) => {
            state.isLoading = false;
            state.currentSubcontract = action.payload;
        });
        builder.addCase(createSubcontract.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Fetch Payment Certificates
        builder.addCase(fetchPaymentCertificates.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchPaymentCertificates.fulfilled, (state, action) => {
            state.isLoading = false;
            state.paymentCertificates = action.payload;
        });
        builder.addCase(fetchPaymentCertificates.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });

        // Fetch Payment Certificate by ID
        builder.addCase(fetchPaymentCertificateById.pending, (state) => {
            state.isLoading = true;
        });
        builder.addCase(fetchPaymentCertificateById.fulfilled, (state, action) => {
            state.isLoading = false;
            state.currentPaymentCertificate = action.payload;
        });
        builder.addCase(fetchPaymentCertificateById.rejected, (state, action: any) => {
            state.isLoading = false;
            state.error = action.payload;
        });
    },
});

export const { clearCurrentSubcontract, clearCurrentPaymentCertificate, clearError } = scSlice.actions;
export default scSlice.reducer;
